import { useEffect, useMemo } from 'react'
import { Box } from '@fe-toolkit/web-components'
import { isRouteErrorResponse, useRouteError, useRouteLoaderData } from '@modern-js/runtime/router'
import { Helmet } from '@modern-js/runtime/head'
import { ThemeProvider, Typography } from '@fe-toolkit/web-components/ssr'
import { createLogger, reportBoundaryError } from '@fe-toolkit/observability/client'

import { customTheme } from '@/config/customTheme'
import { seoTags } from '@/utils/seo/structuredData'

import type { MemberDetails } from '@/api/member/member.types'

const ErrorBoundary = () => {
  // Anything can be thrown, so this is `unknown`: an error response (a Response a loader threw,
  // which the router turns into one), an Error, or any other value, including null.
  const error = useRouteError()
  const response = isRouteErrorResponse(error) ? error : undefined

  const layoutData = useRouteLoaderData('layout') as { memberDetails: MemberDetails } | undefined
  const memberId = layoutData?.memberDetails?.id?.toString() || undefined

  const logger = useMemo(() => createLogger({ name: 'ErrorBoundary', memberId }), [memberId])

  useEffect(() => {
    // The path, not the full URL: a query string can carry tokens or member details.
    const { pathname } = window.location

    // Status, text and path belong in the message rather than as fields: none of the three
    // is in observability's `LogData` allowlist, and a record carrying them is dropped by
    // the log pipeline rather than trimmed. An error response has no stack, so it is logged
    // by its message alone. Anything else is passed as `error`: an Error gives the record its
    // stack trace, and any other value is described safely (a string by its text, an object
    // by its type), never serialised.
    if (isRouteErrorResponse(error)) {
      logger.error(`Route error occurred: ${error.status} ${error.statusText} at ${pathname}`)
    } else {
      logger.error(`Route error occurred at ${pathname}`, { error })
    }
  }, [error, logger])

  // An error thrown while a route renders is caught by the router's boundary, which is this
  // component, sitting inside observabilityRuntimePlugin's root ErrorBoundary. The root boundary
  // never sees it, so report it here to give it the same capture as any other render error
  // (kind "boundary", error_type, stack, and the client-errors metric). An error response is a
  // deliberate loader outcome (404, redirect), not a client error, so it's only logged above.
  useEffect(() => {
    if (error instanceof Error) reportBoundaryError(error)
  }, [error])

  return (
    <ThemeProvider customTheme={customTheme}>
      {/*
        FAAS-771: this boundary has no page.data.ts/page.tsx of its own, so — unlike every
        other route — nothing else here would ever set a robots tag. Modern.js's static
        html.meta default was removed (see modern.config.ts) because it duplicated
        generateSEO's tag on every route that has one; this route is the one place that
        still needs an explicit noindex, since without it the tag is simply absent, which
        crawlers treat as indexable by default.
      */}
      <Helmet>{seoTags({ noindex: true, nofollow: true })}</Helmet>
      <Box>
        <Typography as="h1" variant="speakUp">
          Oops! Something went wrong.
        </Typography>
        {response && (
          <>
            <Typography as="p">Status: {response.status}</Typography>
            <Typography as="p">{response.statusText}</Typography>
          </>
        )}
      </Box>
    </ThemeProvider>
  )
}

export default ErrorBoundary
